import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Download, Lock, Loader2, ChevronRight, CreditCard, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { saveBlob } from "@/lib/download";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BTN_GHOST, DIALOG, isCancel } from "@/components/account/ConfirmIdentity";
import { Section } from "@/components/account/ProfileSection";

const Row = ({ to, icon: Icon, title, text, testid }) => (
  <Link to={to} data-testid={testid} className="flex items-center gap-3 -mx-2 px-2 py-3 rounded-xl hover:bg-white/[0.03] transition-colors">
    <Icon className="h-5 w-5 text-sky-300 shrink-0" />
    <span className="flex-1 min-w-0"><span className="block text-sm text-neutral-100">{title}</span><span className="block text-xs text-neutral-500">{text}</span></span>
    <ChevronRight className="h-4 w-4 text-neutral-600" />
  </Link>
);

function ExportButton() {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await http.get("/account/export", { responseType: "blob" });
      saveBlob(r.data, `brynai-export-${new Date().toISOString().slice(0, 10)}.json`);
      toast.success("Your data is downloading");
    } catch (e) {
      const text = e.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()).detail : formatError(e);
      toast.error(text || "Couldn't prepare your download");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex-1 min-w-0">
        <p className="text-sm text-neutral-100">Download your data</p>
        <p className="text-xs text-neutral-500">Your chats, artifacts, progress, private notes and settings as one file.</p>
      </div>
      <button onClick={run} disabled={busy} className={BTN_GHOST} data-testid="account-export-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}Download</button>
    </div>
  );
}

function DeleteDialog({ getToken, paying, onClose }) {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [word, setWord] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await http.post("/account/delete", { reauth_token: await getToken() });
      toast.success(`Your account will be deleted on ${new Date(data.purge_at).toLocaleDateString()}.`);
      setUser(false);
      navigate("/", { replace: true });
    } catch (e) { if (!isCancel(e)) toast.error(formatError(e)); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={DIALOG} data-testid="delete-account-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Delete your account?</DialogTitle>
          <DialogDescription className="text-neutral-400">You'll be signed out everywhere. Your account and everything in it is deleted for good after 14 days. Sign in before then and choose Restore to keep it.</DialogDescription>
        </DialogHeader>
        {paying && <p className="text-sm text-yellow-200 bg-yellow-300/10 border border-yellow-300/20 rounded-xl p-3" data-testid="delete-account-plan-note">Your plan stops renewing straight away, so you won't be charged again. There's no refund for the time already paid for.</p>}
        <label className="block text-sm text-neutral-400">Type DELETE to confirm
          <Input value={word} onChange={(e) => setWord(e.target.value)} className="mt-1.5 bg-white/5 border-white/10" data-testid="delete-account-confirm-input" />
        </label>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className={BTN_GHOST} data-testid="delete-account-cancel-btn">Keep my account</button>
          <button onClick={go} disabled={busy || word.trim() !== "DELETE"} data-testid="delete-account-confirm-btn"
            className="h-10 px-5 rounded-full bg-red-500 text-white text-sm font-medium hover:bg-red-600 disabled:opacity-50 inline-flex items-center gap-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}Delete account
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DataSection({ account, getToken }) {
  const { ent } = useAuth();
  const [del, setDel] = useState(false);
  const paying = !!ent?.subscription && ent.subscription.status !== "canceled" && !ent.subscription.cancel_at_period_end;
  return (
    <Section title="Your data" testid="account-data">
      <Row to="/app/memory" icon={Lock} title="What I know about you" testid="account-private-notes-link"
        text={`${account.private_notes} private ${account.private_notes === 1 ? "note" : "notes"} Bryn keeps to help you. Only you can see them.`} />
      <Row to="/app/billing" icon={CreditCard} title="Billing" text="Your plan, passes, usage and payments" testid="account-billing-link" />
      <ExportButton />
      {account.is_owner ? <p className="text-xs text-neutral-500" data-testid="account-delete-owner-note">The owner account can't be deleted.</p> : (
        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-white/[0.06]">
          <div className="flex-1 min-w-0">
            <p className="text-sm text-neutral-100">Delete account</p>
            <p className="text-xs text-neutral-500">Removes your account and data after a 14-day grace period.</p>
          </div>
          <button onClick={() => setDel(true)} className="h-10 px-5 rounded-full border border-red-400/30 text-red-300 text-sm hover:bg-red-500/10 inline-flex items-center gap-2" data-testid="account-delete-btn"><Trash2 className="h-4 w-4" />Delete account</button>
        </div>
      )}
      {del && <DeleteDialog getToken={getToken} paying={paying} onClose={() => setDel(false)} />}
    </Section>
  );
}
